import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import {useEffect, useRef, useState} from "react";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import {subWeeks} from "date-fns";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Filters from "./components/filters.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import AttendanceSequenceService from "../../../services/reports/attendance-sequence-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import Content from "./components/content.jsx";
import {Modal} from "../../../components/modal/modal.jsx";
import Button from "../../../components/button/button.jsx";
import InputTextarea from "../../../components/input/input-textarea.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const initialFilter = () => ({
    dtStart: subWeeks(new Date(), 1),
    dtEnd: new Date(),
    attendance: { label: "Todos", value: "all" },
    source: { label: "Todos", value: "all" },
    nature: [],
    nature_groups: [],
    requester: "",
    channel: [],
    street: [],
    place: [],
    neighborhood: [],
    sector: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    attendance: "Atendimento",
    source: "Origem",
    nature: "Naturezas",
    nature_groups: "Grupo de naturezas",
    requester: "Solicitante",
    channel: "Canais",
    street: "Logradouro",
    place: "Local pré-cadastrado",
    neighborhood: "Bairro",
    sector: "Setor",
};

export default function AttendanceSequence(){
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        protocol: {label: "Protocolo", visible: true},
        dispatch: {label: "Despacho", visible: true},
        bo: {label: "BO", visible: true},
        start: {label: "D/H inicial", visible: true},
        update: {label: "Última alteração", visible: true},
        requester: {label: "Solicitante", visible: true},
        channel: {label: "Canal", visible: true},
        nature: {label: "Natureza", visible: true},
        status: {label: "Status", visible: true},
        transcription: {label: "Transcrição", visible: true},
        address: {label: "Endereço", visible: true},
        finalObservation: {label: "Observação final", visible: true}
    }));

    const [boName, setBoName] = useState("BO GCM");
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [openModal, setOpenModal] = useState(false);
    const [selectedTranscription, setSelectedTranscription] = useState("");
    const title = "Sequência de atendimentos";
    const buttonRef = useRef(null);
    const printComponentRef = useRef(null);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels)
        );
    }, [reportData]);



    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, 1, "year");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                attendance: filters.attendance?.value ?? "",
                source: filters.source?.value ?? "",
                nature: JSON.stringify(filters.nature?.map(i => i.value) ?? []),
                nature_groups: JSON.stringify(filters.nature_groups?.map(i => i.value) ?? []),
                requester: filters.requester ?? "",
                channel: JSON.stringify(filters.channel?.map(i => i.value) ?? []),
                street: JSON.stringify(filters.street?.map(i => i.value) ?? []),
                place: JSON.stringify(filters.place?.map(i => i.value) ?? []),
                neighborhood: JSON.stringify(filters.neighborhood?.map(i => i.value) ?? []),
                sector: JSON.stringify(filters.sector?.map(i => i.value) ?? []),
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await AttendanceSequenceService.apply({ ...formattedFilters, filters: auditFilters});
            setReportData(response.data);
            setBoName(response.boName);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório.", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter);
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    };

    const handleOpenTranscriptionModal = (element) => {
        setSelectedTranscription(element?.attendance_transcription);
        setOpenModal(true);
    };

    const handleCloseTranscriptionModal = () => {
        setSelectedTranscription("");
        setOpenModal(false);
    };

    useGlobalKey("Enter", apply);

    return (
        <>
            <SubScroll dependency={[collapsed, reportData]}>
                <div className="flex">
                    <Template isLoading={isLoading}>
                        <Template.Header>
                            <div className="flex justify-between w-full">
                                <Template.Title>{title}</Template.Title>
                                <HeaderButtons
                                    exportable={true}
                                    exportInSheets={true}
                                    printable={true}
                                    headerChange={true}
                                    visibilityColumns={true}
                                    columns={tableColumns}
                                    setColumns={setTableColumns}
                                    printComponentRef={printComponentRef}
                                    tableColumns={tableColumns}
                                    reportData={reportData}
                                    data={reportData?.length}
                                    headerSelected={headerSelected}
                                    setHeaderSelected={setHeaderSelected}
                                />
                            </div>
                        </Template.Header>
                        <Template.Body className={`${collapsed ?  "p-[0]" : ""}`}>
                            <Menu
                                buttonRef={buttonRef}
                                toggleFilters={toggleFilters}
                                fixedTop={fixedTop}
                                collapsed={collapsed}
                                apply={apply}
                                clearSearch={clearSearch}
                            >
                                <Filters setFilters={setFilters} filters={filters} />
                                <AutoDatetime setFilters={setFilters} />
                            </Menu>
                        </Template.Body>
                    </Template>
                    <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                        <Card.Body>
                            <div className={`flex-1 ${!reportData || reportData?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                                <NotAppliedContent reportData={reportData} />
                                <NoDataContent reportData={reportData} data={reportData?.length} />
                                <Content
                                    reportData={reportData}
                                    boName={boName}
                                    data={reportData?.length}
                                    entity={headerSelected}
                                    title={title}
                                    collapsed={collapsed}
                                    setIsLoading={setIsLoading}
                                    printComponentRef={printComponentRef}
                                    onOpenModal={handleOpenTranscriptionModal}
                                    tableColumns={tableColumns}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </SubScroll>

            <Modal
                open={openModal}
                setOpen={setOpenModal}
                title="Transcrição"
                className="w-full max-w-135"
                backdrop="static"
                overflow
            >
                <Modal.Body>
                    <InputTextarea
                        id="transcription"
                        textAreaClass={"h-[20rem]"}
                        value={selectedTranscription}
                        onChange={() => {}}
                        readOnly={true}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleCloseTranscriptionModal}>Ok</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}